import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface LogoSettings {
  logo_path: string | null;
  logo_width: number;
  show_logo: boolean;
}

const DEFAULT_LOGO_SETTINGS: LogoSettings = {
  logo_path: '/images/golden-stream-logo.png',
  logo_width: 180,
  show_logo: true,
};

export function useLogoSettings() {
  return useQuery({
    queryKey: ['site-settings', 'logo_settings'],
    queryFn: async (): Promise<LogoSettings> => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'logo_settings')
        .single();
      
      if (error || !data) {
        return DEFAULT_LOGO_SETTINGS;
      }
      
      return {
        ...DEFAULT_LOGO_SETTINGS,
        ...(data.value as object),
      };
    },
    staleTime: 30000,
  });
}

export function useUpdateLogoSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (settings: Partial<LogoSettings>) => {
      // Get current settings first
      const { data: existingData } = await supabase
        .from('site_settings')
        .select('id, value')
        .eq('key', 'logo_settings')
        .single();
      
      const currentSettings = existingData?.value as unknown as LogoSettings | undefined;
      const newSettings: LogoSettings = {
        ...DEFAULT_LOGO_SETTINGS,
        ...currentSettings,
        ...settings,
      };
      
      if (existingData) {
        const { error } = await supabase
          .from('site_settings')
          .update({ 
            value: newSettings as any,
            updated_at: new Date().toISOString()
          })
          .eq('key', 'logo_settings');
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('site_settings')
          .insert([{ 
            key: 'logo_settings', 
            value: newSettings as any
          }]);
        
        if (error) throw error;
      }
      
      return newSettings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-settings', 'logo_settings'] });
    },
  });
}
